import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { EASE } from "@/components/motion";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (window.__lenis) {
      open ? window.__lenis.stop() : window.__lenis.start();
    }
  }, [open]);

  return (
    <>
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-brand origin-left z-[70]"
        style={{ scaleX: progress }}
      />
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-[60] backdrop-blur-xl bg-paper/75 border-b border-black/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-brand" className="flex items-baseline gap-2">
            <span className="font-display font-semibold text-xl tracking-tight uppercase text-ink">
              AA Gulf
            </span>
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand uppercase">
              FZE
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10" data-testid="nav-desktop">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                    isActive ? "text-brand" : "text-black/50 hover:text-ink"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="nav-cta-enquire"
              className="hidden md:inline-flex group items-center gap-2 bg-brand text-white font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 hover:bg-brand-hover transition-colors duration-300"
            >
              Enquire Now
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              className="md:hidden p-2 text-ink"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[80] bg-paper flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-black/10">
              <span className="font-display font-semibold text-xl tracking-tight uppercase text-ink">
                AA Gulf <span className="font-mono text-[10px] text-brand">FZE</span>
              </span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                className="p-2 text-ink"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.1 + i * 0.08 }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-display text-5xl font-medium uppercase tracking-tight py-3 transition-colors duration-300 ${
                        isActive ? "text-brand" : "text-ink hover:text-brand"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/40">
                Energy Trading · Sharjah, UAE
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
